
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const CollaborationsPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    instagram: '',
    followers: '',
    typeCollab: '',
    budget: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const collaborationTypes = [
    {
      title: 'Marques & Entreprises',
      description: 'Campagnes créatives et stratégies d\'influence sur mesure',
      icon: 'ri-building-line',
      services: [
        'Stratégie de marque digitale',
        'Campagnes d\'influence',
        'Création de contenu branded',
        'Événements et lancements',
      ],
    },
    {
      title: 'Créateurs & Artistes',
      description: 'Collaborations artistiques et projets créatifs innovants',
      icon: 'ri-palette-line',
      services: [
        'Projets photo/vidéo collaboratifs',
        'Créations artistiques mixtes',
        'Expositions et showcases',
        'Résidences créatives',
      ],
    },
    {
      title: 'Médias & Plateformes',
      description: 'Partenariats médiatiques et création de contenu exclusif',
      icon: 'ri-tv-line',
      services: [
        'Contenu éditorial exclusif',
        'Interviews et reportages',
        'Documentaires créatifs',
        'Séries de contenu',
      ],
    },
  ];

  const pastCollaborations = [
    {
      name: 'Nike x ZEUS',
      type: 'Campagne Streetwear',
      image: 'https://public.readdy.ai/ai/img_res/be93c0c6a91b1a2000dc30d417f4df6d.jpg',
      description: 'Campagne exclusive pour le lancement de la collection urbaine',
    },
    {
      name: 'Vogue Paris',
      type: 'Editorial Fashion',
      image: 'https://readdy.ai/api/search-image?query=Vogue%20fashion%20editorial%2C%20high-end%20fashion%20photography%2C%20luxury%20magazine%20shoot%2C%20professional%20fashion%20styling%2C%20artistic%20photography&width=400&height=300&seq=collab-2&orientation=landscape',
      description: 'Shooting éditorial pour le numéro spécial jeunes créateurs',
    },
    {
      name: 'Mercedes AMG',
      type: 'Lifestyle Campaign',
      image: 'https://readdy.ai/api/search-image?query=Mercedes%20AMG%20lifestyle%20campaign%2C%20luxury%20car%20photography%2C%20premium%20automotive%20shoot%2C%20urban%20lifestyle%2C%20professional%20commercial%20photography&width=400&height=300&seq=collab-3&orientation=landscape',
      description: 'Campagne lifestyle mêlant automobile et mode urbaine',
    },
    {
      name: 'Red Bull',
      type: 'Action Sports',
      image: 'https://readdy.ai/api/search-image?query=Red%20Bull%20action%20sports%20collaboration%2C%20extreme%20sports%20photography%2C%20dynamic%20lifestyle%20shoot%2C%20energy%20drink%20campaign%2C%20urban%20sports&width=400&height=300&seq=collab-4&orientation=landscape',
      description: 'Série documentaire sur la culture urbaine parisienne',
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nom || !formData.email || !formData.instagram || !formData.typeCollab) {
      setSubmitMessage('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d44ii0upguuav66rgh50', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString(),
      });

      if (response.ok) {
        setSubmitMessage('Merci ! Votre proposition de collaboration a été reçue.');
        setFormData({
          nom: '',
          email: '',
          instagram: '',
          followers: '',
          typeCollab: '',
          budget: '',
          message: '',
        });
        setShowForm(false);
      } else {
        setSubmitMessage('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } catch (error) {
      setSubmitMessage('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 px-4 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Creative%20collaboration%20workspace%2C%20artistic%20partnership%2C%20brand%20collaboration%20meeting%2C%20professional%20creative%20environment%2C%20orange%20and%20black%20aesthetic&width=1920&height=1080&seq=collaborations-hero&orientation=landscape"
              alt="Collaborations"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/70"></div>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              <span className="text-orange-500">Collaborations</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Créons ensemble des projets exceptionnels
            </p>
            <p className="text-lg text-gray-300 mb-12 max-w-2xl mx-auto">
              Marques, créateurs, artistes — co-créons vos projets. 
              Nous transformons les idées en expériences mémorables qui marquent les esprits.
            </p>

            <button
              onClick={() => setShowForm(true)}
              className="inline-block px-8 py-4 bg-orange-500 text-black font-semibold rounded-lg hover:bg-orange-600 transition-all duration-300 transform hover:scale-105"
            >
              Proposer une collaboration
            </button>
          </div>
        </section>

        {/* Collaboration Types */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-16">
              Types de <span className="text-orange-500">Collaborations</span>
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {collaborationTypes.map((type, index) => (
                <div
                  key={index}
                  className="bg-gray-900 rounded-lg p-8 hover:transform hover:scale-105 transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mb-6">
                    <i className={`${type.icon} text-3xl text-orange-500`}></i>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4">{type.title}</h3>
                  <p className="text-gray-300 mb-6">{type.description}</p>
                  
                  <ul className="space-y-3">
                    {type.services.map((service, i) => (
                      <li key={i} className="flex items-start text-gray-300">
                        <i className="ri-check-line text-orange-500 mr-3 mt-1 flex-shrink-0"></i>
                        {service}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Past Collaborations */}
        <section className="py-20 px-4 bg-gray-900">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-16">
              Collaborations <span className="text-orange-500">Réalisées</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {pastCollaborations.map((collab, index) => (
                <div
                  key={index}
                  className="bg-gray-800 rounded-lg overflow-hidden hover:transform hover:scale-105 transition-all duration-300"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={collab.image}
                      alt={collab.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-xl font-bold text-white">{collab.name}</h3>
                      <span className="bg-orange-500/20 text-orange-500 px-3 py-1 rounded-full text-sm">
                        {collab.type}
                      </span>
                    </div>
                    <p className="text-gray-300">{collab.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-16">
              Notre <span className="text-orange-500">Processus</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                {
                  step: '1',
                  title: 'Écoute',
                  description: 'Nous analysons vos besoins et objectifs',
                  icon: 'ri-ear-line',
                },
                {
                  step: '2',
                  title: 'Création',
                  description: 'Développement du concept créatif sur mesure',
                  icon: 'ri-lightbulb-line',
                },
                {
                  step: '3',
                  title: 'Production',
                  description: 'Réalisation avec notre équipe d\'experts',
                  icon: 'ri-camera-line',
                },
                {
                  step: '4',
                  title: 'Livraison',
                  description: 'Finalisation et livraison du projet',
                  icon: 'ri-check-line',
                },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${step.icon} text-2xl text-black`}></i>
                  </div>
                  <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-orange-500 font-bold">{step.step}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-gray-400 text-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Collaborate */}
        <section className="py-20 px-4 bg-gray-900">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-16">
              Pourquoi collaborer avec <span className="text-orange-500">nous</span> ?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: 'ri-award-line',
                  title: 'Expertise Reconnue',
                  description: 'Plus de 50 collaborations réussies avec des marques prestigieuses',
                },
                {
                  icon: 'ri-team-line',
                  title: 'Équipe Créative',
                  description: 'Photographes, vidéastes et créatifs passionnés à votre service',
                },
                {
                  icon: 'ri-rocket-line',
                  title: 'Innovation',
                  description: 'Approche créative unique mêlant tradition et modernité',
                },
                {
                  icon: 'ri-global-line',
                  title: 'Rayonnement',
                  description: 'Visibilité internationale grâce à notre réseau étendu',
                },
              ].map((feature, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${feature.icon} text-2xl text-orange-500`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              Prêt à créer quelque chose d'<span className="text-orange-500">exceptionnel</span> ?
            </h2>
            <p className="text-xl text-gray-300 mb-12">
              Contactez-nous pour discuter de votre projet et découvrir comment nous pouvons le réaliser ensemble.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                onClick={() => setShowForm(true)}
                className="px-8 py-4 bg-orange-500 text-black font-semibold rounded-lg hover:bg-orange-600 transition-all duration-300 transform hover:scale-105"
              >
                Proposer une collaboration
              </button>
              <a
                href="mailto:adel@vybeparis.com"
                className="px-8 py-4 bg-transparent border-2 border-orange-500 text-orange-500 font-semibold rounded-lg hover:bg-orange-500 hover:text-black transition-all duration-300"
              >
                adel@vybeparis.com
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Collaboration Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Proposer une Collaboration</h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-white"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Nom complet *</label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Instagram *</label>
                  <input
                    type="text"
                    name="instagram"
                    value={formData.instagram}
                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    required
                    placeholder="@votre_compte"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Nombre de followers</label>
                  <select
                    name="followers"
                    value={formData.followers}
                    onChange={(e) => setFormData({ ...formData, followers: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  >
                    <option value="">Sélectionner</option>
                    <option value="< 10K">&lt; 10K</option>
                    <option value="10K - 50K">10K - 50K</option>
                    <option value="50K - 200K">50K - 200K</option>
                    <option value="200K - 500K">200K - 500K</option>
                    <option value="> 500K">&gt; 500K</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Type de collaboration *</label>
                  <select
                    name="typeCollab"
                    value={formData.typeCollab}
                    onChange={(e) => setFormData({ ...formData, typeCollab: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  >
                    <option value="">Sélectionner</option>
                    <option value="Story Instagram">Story Instagram</option>
                    <option value="Post + Story">Post + Story</option>
                    <option value="Vidéo Reel / TikTok">Vidéo Reel / TikTok</option>
                    <option value="Mention multi-plateformes">Mention multi-plateformes</option>
                    <option value="Campagne créative">Campagne créative</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Budget proposé</label>
                  <input
                    type="text"
                    name="budget"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    placeholder="Ex: 1000€"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Message (optionnel)</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none resize-none"
                    placeholder={`Décrivez votre projet... (${formData.message.length}/500)`}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-6 px-6 py-3 bg-orange-500 text-black font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer la proposition'}
              </button>
            </form>

            {submitMessage && (
              <div className={`mt-4 p-4 rounded-lg ${
                submitMessage.includes('Merci') ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {submitMessage}
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CollaborationsPage;
